import { useCallback, useEffect, useState } from "react";
import { CheckCircle2, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import { useProjectCtx } from "@/context/ProjectContext";
import { PageHeader } from "@/components/PageHeader";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { getErrorMessage } from "@/lib/errors";
import { WBSItemsTable } from "@/pages/project/wbs/WBSItemsTable";
import { WeightageEditor } from "@/pages/project/wbs/WeightageEditor";

const CATEGORIES = ["ENGINEERING", "PROCUREMENT", "CONSTRUCTION"];
const CATEGORY_KEYS = [...CATEGORIES, "APPROVAL"];

export default function WBSPage() {
  const { projectId, isProjectManager } = useProjectCtx();
  const [category, setCategory] = useState("ENGINEERING");
  const [validation, setValidation] = useState([]);
  const [catWeights, setCatWeights] = useState({});
  const [savingSplit, setSavingSplit] = useState(false);

  const loadValidation = useCallback(async () => {
    const { data } = await api.get(`/projects/${projectId}/weightages/validate`);
    setValidation(data);
  }, [projectId]);

  const loadCatWeights = useCallback(async () => {
    const { data } = await api.get(`/projects/${projectId}/weightages`);
    const cat = {};
    data.filter((w) => w.scope === "CATEGORY").forEach((w) => { cat[w.key] = w.weight; });
    setCatWeights({ ENGINEERING: 0.15, PROCUREMENT: 0.2, CONSTRUCTION: 0.6, APPROVAL: 0.05, ...cat });
  }, [projectId]);

  useEffect(() => { loadValidation(); loadCatWeights(); }, [loadValidation, loadCatWeights]);

  const saveSplit = async () => {
    setSavingSplit(true);
    try {
      await api.put(`/projects/${projectId}/weightages`, {
        weights: CATEGORY_KEYS.map((k) => ({ scope: "CATEGORY", key: k, weight: Number(catWeights[k] || 0) })),
      });
      toast.success("Category split saved");
      loadValidation();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSavingSplit(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="wbs-page">
      <PageHeader title="WBS & Weightages" description="Manage the work breakdown structure and how each item contributes to overall progress." />

      <div className="rounded-xl border border-border bg-card p-4" data-testid="weightage-validation-summary">
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-muted-foreground">Weightage Health Check</h2>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4">
          {validation.map((v) => (
            <div key={v.category} className="flex items-start gap-2 rounded-lg border border-border p-2.5" data-testid={`validation-${v.category}`}>
              {v.is_valid ? <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-400" /> : <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-400" />}
              <div>
                <p className="text-xs font-semibold text-foreground">{v.category} · {Number(v.weight_sum).toFixed(2)}</p>
                <p className="text-[11px] text-muted-foreground">{v.message}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-xl border border-border bg-card p-4" data-testid="category-split-editor">
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-muted-foreground">Overall Category Split</h2>
        <div className="flex flex-wrap items-end gap-3">
          {CATEGORY_KEYS.map((k) => (
            <div key={k}>
              <Label className="text-xs">{k}</Label>
              <Input
                type="number" step="0.01" min="0" max="1"
                disabled={!isProjectManager}
                value={catWeights[k] ?? ""}
                onChange={(e) => setCatWeights({ ...catWeights, [k]: e.target.value })}
                data-testid={`category-weight-input-${k}`}
                className="mt-1 w-24"
              />
            </div>
          ))}
          {isProjectManager && (
            <Button size="sm" disabled={savingSplit} onClick={saveSplit} data-testid="save-category-split-button">Save split</Button>
          )}
        </div>
      </div>

      <Tabs value={category} onValueChange={setCategory}>
        <TabsList data-testid="wbs-category-tabs">
          {CATEGORIES.map((c) => <TabsTrigger key={c} value={c} data-testid={`wbs-tab-${c}`}>{c}</TabsTrigger>)}
        </TabsList>
        <TabsContent value={category} className="space-y-6">
          <WBSItemsTable projectId={projectId} category={category} canEdit={isProjectManager} onChanged={() => { loadValidation(); }} />
          <WeightageEditor projectId={projectId} category={category} canEdit={isProjectManager} onSaved={loadValidation} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
